'use client';

import { c as _c } from "react/compiler-runtime";
import { observer } from 'mobx-react';
import React, { FC, useContext, useEffect, useMemo, forwardRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { enGB, th, vi, ja } from 'date-fns/locale';
import { DateTime } from 'luxon';
import { FaUndoAlt } from '@react-icons/all-files/fa/FaUndoAlt';
import { ProductionStats } from './stats';
import { ProductionGraph } from './graph';
import { useTranslation } from 'react-i18next';
import { YieldStatsTransformed } from '@services/responseTypes';
import { InfoCircle } from '@components/icons';
import { RootStoreContext } from '@stores/rootStore';
export type ProductionInterval = 'day' | 'week' | 'month';
const INTERVAL_LABEL_KEY: Record<ProductionInterval, string> = {
  day: 'daily',
  week: 'weekly',
  month: 'monthly'
};
const INTERVAL_DEFAULT_LABEL: Record<ProductionInterval, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly'
};
interface DateRangeFilterProps {
  from: string | null;
  to: string | null;
  onChange: (from: string | null, to: string | null) => void;
  onReset: () => void;
}
interface DateFieldButtonProps {
  onClick?: () => void;
  label: string;
  displayValue: string;
  placeholder: string;
}
const DateFieldButton = forwardRef<HTMLButtonElement, DateFieldButtonProps>((t0, ref) => {
  const $ = _c(12);
  const {
    onClick,
    label,
    displayValue,
    placeholder
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label) {
    t2 = <span className="text-xs font-normal text-gray-500">{label}</span>;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = displayValue || placeholder;
  let t4;
  if ($[3] !== t3) {
    t4 = <span className="truncate">{t3}</span>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <span className="flex min-w-0 flex-col leading-tight">{t2}{t4}</span>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onClick || $[9] !== ref || $[10] !== t5) {
    t6 = <button ref={ref} type="button" onClick={onClick} className={"\n        inline-flex w-44 items-center gap-x-2 rounded-lg border\n        border-gray-200 bg-white px-3 py-1.5 text-left text-sm font-medium\n        text-secondary-300 transition-colors\n        hover:bg-gray-50\n      "}>{t1}{t5}</button>;
    $[8] = onClick;
    $[9] = ref;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
});
DateFieldButton.displayName = 'DateFieldButton';
const DateRangeFilter: FC<DateRangeFilterProps> = t0 => {
  const $ = _c(78);
  const {
    from,
    to,
    onChange,
    onReset
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    userStore
  } = useContext(RootStoreContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp, t1);
  let T0;
  let T1;
  let endDate;
  let formatDate;
  let handleEndChange;
  let startDate;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== from || $[2] !== onChange || $[3] !== t || $[4] !== to || $[5] !== userStore.user.settings.lang) {
    startDate = from ? new Date(from) : null;
    let t12;
    if ($[22] !== to) {
      t12 = to ? new Date(to) : null;
      $[22] = to;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    endDate = t12;
    formatDate = _temp2;
    let t13;
    if ($[24] !== endDate || $[25] !== onChange || $[26] !== to) {
      t13 = next => {
        const nextFrom = next ? DateTime.fromJSDate(next).toISODate() : null;
        let nextTo = to;
        if (next && endDate && next > endDate) {
          nextTo = null;
        }
        onChange(nextFrom, nextTo);
      };
      $[24] = endDate;
      $[25] = onChange;
      $[26] = to;
      $[27] = t13;
    } else {
      t13 = $[27];
    }
    const handleStartChange = t13;
    let t14;
    if ($[28] !== from || $[29] !== onChange) {
      t14 = next_0 => {
        const nextTo_0 = next_0 ? DateTime.fromJSDate(next_0).toISODate() : null;
        onChange(from, nextTo_0);
      };
      $[28] = from;
      $[29] = onChange;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    handleEndChange = t14;
    t11 = "inline-flex items-center gap-x-2";
    T1 = DatePicker;
    t4 = startDate;
    t5 = handleStartChange;
    t6 = true;
    t7 = startDate;
    t8 = endDate;
    t9 = true;
    t10 = userStore.user.settings.lang;
    T0 = DateFieldButton;
    if ($[31] !== t) {
      t2 = t("startDate", {
        defaultValue: "Start"
      });
      $[31] = t;
      $[32] = t2;
    } else {
      t2 = $[32];
    }
    t3 = formatDate(startDate);
    $[1] = from;
    $[2] = onChange;
    $[3] = t;
    $[4] = to;
    $[5] = userStore.user.settings.lang;
    $[6] = T0;
    $[7] = T1;
    $[8] = endDate;
    $[9] = formatDate;
    $[10] = handleEndChange;
    $[11] = startDate;
    $[12] = t10;
    $[13] = t11;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[6];
    T1 = $[7];
    endDate = $[8];
    formatDate = $[9];
    handleEndChange = $[10];
    startDate = $[11];
    t10 = $[12];
    t11 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t12;
  if ($[33] !== t) {
    t12 = t("selectDate", {
      defaultValue: "Select date"
    });
    $[33] = t;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== T0 || $[36] !== t12 || $[37] !== t2 || $[38] !== t3) {
    t13 = <T0 label={t2} displayValue={t3} placeholder={t12} />;
    $[35] = T0;
    $[36] = t12;
    $[37] = t2;
    $[38] = t3;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== T1 || $[41] !== t10 || $[42] !== t13 || $[43] !== t4 || $[44] !== t5 || $[45] !== t6 || $[46] !== t7 || $[47] !== t8 || $[48] !== t9) {
    t14 = <T1 selected={t4} onChange={t5} selectsStart={t6} startDate={t7} endDate={t8} fixedHeight={t9} locale={t10} customInput={t13} calendarClassName="date-picker" />;
    $[40] = T1;
    $[41] = t10;
    $[42] = t13;
    $[43] = t4;
    $[44] = t5;
    $[45] = t6;
    $[46] = t7;
    $[47] = t8;
    $[48] = t9;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  const t15 = startDate ?? undefined;
  const t16 = userStore.user.settings.lang;
  let t17;
  if ($[50] !== t) {
    t17 = t("endDate", {
      defaultValue: "End"
    });
    $[50] = t;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== endDate || $[53] !== formatDate) {
    t18 = formatDate(endDate);
    $[52] = endDate;
    $[53] = formatDate;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t) {
    t19 = t("selectDate", {
      defaultValue: "Select date"
    });
    $[55] = t;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t17 || $[58] !== t18 || $[59] !== t19) {
    t20 = <DateFieldButton label={t17} displayValue={t18} placeholder={t19} />;
    $[57] = t17;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== endDate || $[62] !== handleEndChange || $[63] !== startDate || $[64] !== t15 || $[65] !== t20 || $[66] !== userStore.user.settings.lang) {
    t21 = <DatePicker selected={endDate} onChange={handleEndChange} selectsEnd={true} startDate={startDate} endDate={endDate} minDate={t15} fixedHeight={true} locale={t16} customInput={t20} calendarClassName="date-picker" />;
    $[61] = endDate;
    $[62] = handleEndChange;
    $[63] = startDate;
    $[64] = t15;
    $[65] = t20;
    $[66] = userStore.user.settings.lang;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== endDate || $[69] !== onReset || $[70] !== startDate || $[71] !== t) {
    t22 = (startDate || endDate) && <button type="button" onClick={onReset} aria-label={t("resetDateRange", {
      defaultValue: "Reset date range"
    })} className={"\n            inline-flex h-8 w-8 items-center justify-center rounded-lg border\n            border-gray-200 bg-white text-gray-500 transition-colors\n            hover:bg-gray-50 hover:text-secondary-300\n          "}><FaUndoAlt className="h-3 w-3" /></button>;
    $[68] = endDate;
    $[69] = onReset;
    $[70] = startDate;
    $[71] = t;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== t11 || $[74] !== t14 || $[75] !== t21 || $[76] !== t22) {
    t23 = <div className={t11}>{t14}{t21}{t22}</div>;
    $[73] = t11;
    $[74] = t14;
    $[75] = t21;
    $[76] = t22;
    $[77] = t23;
  } else {
    t23 = $[77];
  }
  return t23;
};
export const Production: FC<{
  stats?: YieldStatsTransformed;
  interval: ProductionInterval;
  onIntervalChange: (interval: ProductionInterval) => void;
  dateRange?: {
    from: string | null;
    to: string | null;
  };
  onDateRangeChange?: (from: string | null, to: string | null) => void;
  onDateRangeReset?: () => void;
}> = observer(({
  stats,
  interval,
  onIntervalChange,
  dateRange,
  onDateRangeChange,
  onDateRangeReset
}) => {
  const {
    t
  } = useTranslation();
  const {
    uiStore
  } = useContext(RootStoreContext);
  const showSupplyChainView = uiStore.currentOrganization?.hasSupplyChainFeature ?? false;
  const intervalToggle = <div role="tablist" aria-label={t('productionInterval', {
    defaultValue: 'Production interval'
  })} className={`
          inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-sm
          font-medium
          print:hidden
        `}>
        {showSupplyChainView && <>
            {(['day', 'week', 'month'] as ProductionInterval[]).map(key => {
        const active = interval === key;
        return <button key={key} type="button" role="tab" aria-selected={active} onClick={() => onIntervalChange(key)} className={`
                rounded-md px-3 py-1.5 transition-colors
                ${active ? 'bg-secondary-300 text-white' : 'text-secondary-300 hover:bg-gray-100'}
              `}>
                  {t(INTERVAL_LABEL_KEY[key], {
            defaultValue: INTERVAL_DEFAULT_LABEL[key]
          })}
                </button>;
      })}
          </>}
      </div>;
  return <>
        <div className="flex flex-row items-center gap-2">
          <h1 className={`
              block text-3xl
              print:hidden print:text-4xl print:font-bold
            `}>
            {t('production')}
          </h1>

          <div className="print:hidden" data-tooltip-id="mobile-enabled-tooltip" data-tooltip-content={t('productionGraph_summary')}>
            <InfoCircle height={18} width={18} stroke="#fff" fill="#727D9F" />
          </div>
        </div>

        <div className={`
            flex w-full flex-col gap-4
            lg:flex-row-reverse
            print:flex-col
          `}>
          <div className={`
              w-full
              lg:w-1/4
              print:w-full print:px-12
            `}>
            <ProductionStats total={stats?.actual.total} percentageOfTarget={stats?.actual.percentageOfTarget} averageYield={stats?.actual.averageYield} percentageOfTargetYield={stats?.actual.percentageOfTargetYield} />
          </div>

          <div className={`
              keep-together w-full
              lg:w-3/4
              print:ml-32 print:flex print:flex-col print:justify-center
            `}>
            <h1 className={`
                hidden text-3xl
                print:my-8 print:ml-4 print:block print:text-4xl print:font-bold
              `}>
              {t('production')}
            </h1>
            <div className={`
                mb-2 flex flex-wrap items-center justify-end gap-2
                print:hidden
              `}>
              {showSupplyChainView && dateRange && onDateRangeChange && <DateRangeFilter from={dateRange.from} to={dateRange.to} onChange={onDateRangeChange} onReset={onDateRangeReset ?? (() => {})} />}
              {intervalToggle}
            </div>
            <ProductionGraph actual={stats?.actual} predicted={stats?.forecasted} targetMax={stats?.targetMax} targetMin={stats?.targetMin} interval={interval} />
            <hr className={`
                my-20 hidden h-px w-full border-silver bg-gray-300
                print:block
              `} />
          </div>
        </div>
      </>;
});
function _temp() {
  registerLocale("en", enGB);
  registerLocale("th", th);
  registerLocale("vi", vi);
  registerLocale("ja", ja);
}
function _temp2(d) {
  return d ? DateTime.fromJSDate(d).toFormat("LLL d, yyyy") : "";
}